import React, {
    useState,
    useRef,
    useEffect,
    useMemo,
    useCallback,
    useContext,
    useId,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { OverlayOwnerContext, focusableElements } from "@/components/overlay";

export interface SelectOption<T = string | number> {
    value: T;
    label: string;
    subtitle?: string;
    icon?:
        | React.ComponentType<{ size?: number; className?: string }>
        | React.ReactNode
        | string;
    iconColor?: string;
    iconPosition?: "left" | "right";
}

export interface SelectProps<T = string | number> {
    value: T | T[];
    onChange: (value: any) => void;
    options: SelectOption<T>[];
    multiple?: boolean;
    label?: string;
    ariaLabel?: string;
    placeholder?: string;
    placeholderClassName?: string;
    searchable?: boolean;
    onSearchChange?: (search: string) => void;
    searchPlaceholder?: string;
    icon?: React.ComponentType<{ size?: number; className?: string }>;
    iconOnly?: boolean;
    className?: string;
    dropdownClassName?: string;
    size?: "sm" | "md" | "lg";
    disabled?: boolean;
    clearable?: boolean;
    placement?: "top" | "bottom" | "auto";
    align?: "left" | "right";
    noarrow?: boolean;
    dropdownMinWidth?: number;
    full?: boolean;
    emptyValues?: (string | number)[];
    actions?: React.ReactNode;
    required?: boolean;
    error?: string;
}

export function Select<T extends string | number = string | number>({
    value,
    onChange,
    options = [],
    multiple = false,
    label = "",
    ariaLabel,
    placeholder = "",
    placeholderClassName = "",
    searchable = false,
    onSearchChange,
    searchPlaceholder,
    icon: LeadingIcon,
    iconOnly = false,
    className = "",
    dropdownClassName = "",
    size = "md",
    disabled = false,
    clearable = false,
    placement = "auto",
    align = "left",
    noarrow = false,
    dropdownMinWidth,
    full = false,
    emptyValues = ["", "0", 0, "all"],
    actions,
    required = false,
    error,
}: SelectProps<T>): React.ReactElement {
    const isFullWidth = Boolean(
        full ||
            className
                .split(/\s+/)
                .some((token) => token === "w-full" || token === "!w-full")
    );
    const { t } = useTranslation("global");
    const owner = useContext(OverlayOwnerContext);
    const listId = useId();
    const [activeIndex, setActiveIndex] = useState(0);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isClosing, setIsClosing] = useState<boolean>(false);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [computedPlacement, setComputedPlacement] = useState<
        "top" | "bottom"
    >(placement === "top" ? "top" : "bottom");

    const [dropdownCoords, setDropdownCoords] = useState<{
        top?: number;
        bottom?: number;
        left?: number;
        right?: number;
        width: number;
        placement: "top" | "bottom";
    }>({
        width: 0,
        placement: placement === "top" ? "top" : "bottom",
    });

    const selectedValues: T[] = useMemo(() => {
        if (!multiple) {
            return [];
        }

        if (Array.isArray(value)) {
            return value;
        }

        if (value !== "" && value !== undefined && value !== null) {
            return [value as T];
        }

        return [];
    }, [multiple, value]);

    const selectedOptions = useMemo(() => {
        if (multiple) {
            return options.filter((opt) =>
                selectedValues.some((v) => String(v) === String(opt.value))
            );
        }

        return [];
    }, [multiple, options, selectedValues]);

    const selectedOption = useMemo(() => {
        if (multiple) {
            return null;
        }

        return options.find((opt) => String(opt.value) === String(value));
    }, [multiple, options, value]);

    const filteredOptions = useMemo(() => {
        if (onSearchChange) {
            return options;
        }
        if (!searchable || !searchTerm.trim()) {
            return options;
        }

        const needle = searchTerm.toLowerCase();
        return options.filter((opt) => {
            const matchLabel = opt.label.toLowerCase().includes(needle);
            const matchSubtitle = opt.subtitle
                ? opt.subtitle.toLowerCase().includes(needle)
                : false;
            return matchLabel || matchSubtitle;
        });
    }, [options, searchable, searchTerm, onSearchChange]);
    useEffect(() => {
        onSearchChange?.(searchTerm);
    }, [searchTerm]);

    const renderOptionIcon = (
        opt: SelectOption<T>,
        isSelected: boolean,
        iconSize: number = 14
    ): React.ReactNode => {
        if (!opt.icon) {
            return null;
        }

        const colorClass =
            opt.iconColor ||
            (isSelected
                ? "text-primary dark:text-white"
                : "text-slate-400 dark:text-zinc-400");

        if (typeof opt.icon === "string") {
            return (
                <Icon
                    value={opt.icon}
                    size={iconSize}
                    className={`${colorClass} shrink-0`}
                />
            );
        }

        if (React.isValidElement(opt.icon)) {
            return (
                <span className="shrink-0 flex items-center">{opt.icon}</span>
            );
        }

        const IconComponent = opt.icon as React.ComponentType<{
            size?: number;
            className?: string;
        }>;

        return (
            <IconComponent
                size={iconSize}
                className={`${colorClass} shrink-0`}
            />
        );
    };

    const updateDropdownPosition = useCallback((): void => {
        const anchorEl = triggerRef.current || containerRef.current;
        if (!anchorEl) {
            return;
        }

        const rect = anchorEl.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 240;

        let shouldPlaceTop = false;
        if (placement === "top") {
            shouldPlaceTop = true;
        } else {
            if (
                placement !== "bottom" &&
                spaceBelow < dropdownHeight &&
                spaceAbove > spaceBelow
            ) {
                shouldPlaceTop = true;
            }
        }

        const targetPlacement: "top" | "bottom" = shouldPlaceTop
            ? "top"
            : "bottom";
        setComputedPlacement(targetPlacement);

        const measuredWidth = dropdownRef.current
            ? dropdownRef.current.offsetWidth
            : dropdownMinWidth || 320;
        const spaceRight = window.innerWidth - rect.left - 16;
        const spaceLeft = rect.right - 16;

        let shouldAlignRight = align === "right";
        if (align !== "right") {
            if (spaceRight < measuredWidth && spaceLeft >= spaceRight) {
                shouldAlignRight = true;
            } else {
                if (spaceRight < 240 && spaceLeft > spaceRight) {
                    shouldAlignRight = true;
                }
            }
        }

        const horizontalLeft = shouldAlignRight
            ? undefined
            : Math.max(8, Math.min(rect.left, window.innerWidth - 120));
        const horizontalRight = shouldAlignRight
            ? Math.max(
                  8,
                  Math.min(
                      window.innerWidth - rect.right,
                      window.innerWidth - 120
                  )
              )
            : undefined;

        setDropdownCoords({
            top: shouldPlaceTop ? undefined : Math.round(rect.bottom + 6),
            bottom: shouldPlaceTop
                ? Math.round(window.innerHeight - rect.top + 6)
                : undefined,
            left:
                horizontalLeft !== undefined
                    ? Math.round(horizontalLeft)
                    : undefined,
            right:
                horizontalRight !== undefined
                    ? Math.round(horizontalRight)
                    : undefined,
            width: Math.round(rect.width),
            placement: targetPlacement,
        });
    }, [placement, align, dropdownMinWidth]);

    const closeDropdown = useCallback(
        (restoreFocus = false): void => {
            if (!isOpen || isClosing) {
                return;
            }

            setIsClosing(true);
            if (restoreFocus) {
                triggerRef.current?.focus();
            }
            if (closeTimerRef.current) {
                clearTimeout(closeTimerRef.current);
            }

            closeTimerRef.current = setTimeout(() => {
                setIsOpen(false);
                setIsClosing(false);
                setSearchTerm("");
            }, 160);
        },
        [isOpen, isClosing]
    );

    useEffect(() => {
        return () => {
            if (closeTimerRef.current) {
                clearTimeout(closeTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isOpen && !isClosing) {
            return;
        }

        updateDropdownPosition();

        const frameId = requestAnimationFrame(() => {
            updateDropdownPosition();
        });

        const handleWindowChange = (): void => {
            updateDropdownPosition();
        };

        const handleClickOutside = (event: MouseEvent): void => {
            const target = event.target as Node;
            if (
                containerRef.current &&
                !containerRef.current.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                closeDropdown();
            }
        };

        window.addEventListener("resize", handleWindowChange);
        window.addEventListener("scroll", handleWindowChange, true);
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            cancelAnimationFrame(frameId);
            window.removeEventListener("resize", handleWindowChange);
            window.removeEventListener("scroll", handleWindowChange, true);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, isClosing, updateDropdownPosition, closeDropdown]);

    const sizeClasses =
        {
            sm: "h-8 min-h-[32px] py-1 px-2.5 text-xs rounded-lg",
            md: "min-h-[38px] py-2.5 px-3.5 text-xs rounded-lg",
            lg: "min-h-[44px] py-3 px-4 text-sm rounded-lg",
        }[size] || "min-h-[38px] py-2.5 px-3.5 text-xs rounded-lg";

    const handleToggle = (e?: React.MouseEvent): void => {
        if (e) {
            e.stopPropagation();
        }

        if (disabled) {
            return;
        }

        if (!isOpen || isClosing) {
            if (closeTimerRef.current) {
                clearTimeout(closeTimerRef.current);
            }
            setIsClosing(false);
            setSearchTerm("");
            updateDropdownPosition();
            setIsOpen(true);
        } else {
            closeDropdown();
        }
    };

    const handleSelect = (optionValue: T): void => {
        if (multiple) {
            const current = Array.isArray(value) ? [...value] : [];
            const index = current.findIndex(
                (v) => String(v) === String(optionValue)
            );
            let next: T[];
            if (index >= 0) {
                next = current.filter((_, i) => i !== index);
            } else {
                next = [...current, optionValue];
            }

            onChange(next);
            return;
        }

        onChange(optionValue);
        closeDropdown(true);
    };

    useEffect(() => {
        if (isOpen && !isClosing) {
            const selected = filteredOptions.findIndex(
                (option) => String(option.value) === String(value)
            );
            setActiveIndex(Math.max(0, selected));
        }
    }, [isOpen, searchTerm]);
    useEffect(() => {
        if (isOpen) {
            document
                .getElementById(`${listId}-option-${activeIndex}`)
                ?.scrollIntoView({ block: "nearest" });
        }
    }, [isOpen, activeIndex, listId]);

    const handleKeyboard = (event: React.KeyboardEvent): void => {
        if (event.key === "Escape" && isOpen) {
            event.preventDefault();
            event.stopPropagation();
            closeDropdown(true);
            return;
        }
        if (event.key === "Tab" && isOpen) {
            event.preventDefault();
            const trigger = triggerRef.current;
            if (trigger) {
                const items = focusableElements(
                    trigger.closest<HTMLElement>("[role=dialog]") ||
                        document.body
                ).filter((element) => !dropdownRef.current?.contains(element));
                const index = items.indexOf(trigger);
                items[
                    (index + (event.shiftKey ? -1 : 1) + items.length) %
                        items.length
                ]?.focus();
            }
            closeDropdown();
            return;
        }
        if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
            event.preventDefault();
            event.stopPropagation();
            if (!isOpen) {
                handleToggle();
                return;
            }
            const last = filteredOptions.length - 1;
            setActiveIndex((current) =>
                event.key === "Home"
                    ? 0
                    : event.key === "End"
                      ? Math.max(0, last)
                      : Math.max(
                            0,
                            Math.min(
                                last,
                                current + (event.key === "ArrowDown" ? 1 : -1)
                            )
                        )
            );
            return;
        }
        if (
            isOpen &&
            (event.key === "Enter" ||
                (event.key === " " &&
                    !(event.target instanceof HTMLInputElement)))
        ) {
            event.preventDefault();
            event.stopPropagation();
            const option = filteredOptions[activeIndex];
            if (option) {
                handleSelect(option.value);
            }
        }
    };

    const handleClear = (e: React.SyntheticEvent): void => {
        e.stopPropagation();
        if (multiple) {
            onChange([]);
            setSearchTerm("");
            return;
        }

        const emptyVal = (typeof value === "number" ? 0 : "") as unknown as T;
        onChange(emptyVal);
        setSearchTerm("");
    };

    const isEmptyValue = (val: T | T[]): boolean => {
        if (Array.isArray(val)) {
            return false;
        }
        return (
            String(val) === "" ||
            String(val) === "0" ||
            emptyValues.some((ev) => String(ev) === String(val))
        );
    };

    const hasSelection = multiple
        ? selectedOptions.length > 0
        : Boolean(selectedOption && !isEmptyValue(value as T));

    const displayLabel = useMemo((): string => {
        if (multiple) {
            if (selectedOptions.length === 0) {
                return placeholder;
            }

            if (selectedOptions.length === 1) {
                return selectedOptions[0].label;
            }

            return `${selectedOptions.length} ${selectedOptions.length === 1 ? t("common.selected_singular") : t("common.selected")}`;
        }

        return selectedOption ? selectedOption.label : placeholder;
    }, [multiple, selectedOptions, selectedOption, placeholder, t]);

    const activePlacement = dropdownCoords.placement;
    const animationClass = isClosing
        ? activePlacement === "top"
            ? "select-dropdown--closing-top"
            : "select-dropdown--closing-bottom"
        : activePlacement === "top"
          ? "select-dropdown--open-top"
          : "select-dropdown--open-bottom";

    return (
        <div
            className={`relative ${isFullWidth ? "w-full block" : "inline-block"} ${className}`}
            ref={containerRef}
        >
            {(label || actions) && (
                <div className="flex items-end justify-between mb-1">
                    {label && (
                        <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300">
                            {label}{" "}
                            {required && (
                                <span className="text-primary">*</span>
                            )}
                        </label>
                    )}
                    {actions && (
                        <div className="flex items-end gap-1.5 leading-none ml-auto translate-y-[2px]">
                            {actions}
                        </div>
                    )}
                </div>
            )}

            {/* Trigger Button */}
            <div className={`relative ${isFullWidth ? "w-full" : "w-fit"}`}>
                <button
                    ref={triggerRef}
                    type="button"
                    disabled={disabled}
                    onClick={handleToggle}
                    onKeyDown={handleKeyboard}
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-controls={isOpen ? listId : undefined}
                    aria-activedescendant={
                        isOpen && filteredOptions[activeIndex]
                            ? `${listId}-option-${activeIndex}`
                            : undefined
                    }
                    aria-invalid={Boolean(error) || undefined}
                    aria-describedby={error ? `${listId}-error` : undefined}
                    data-form-value={JSON.stringify(value)}
                    aria-label={
                        ariaLabel || label || placeholder || t("common.search")
                    }
                    aria-required={required || undefined}
                    aria-expanded={isOpen}
                    className={`select-trigger ${sizeClasses} ${iconOnly ? "!h-9 !min-h-9 !w-9 !min-w-9 !p-0 !justify-center !border-transparent !bg-transparent hover:!bg-slate-100 dark:hover:!bg-white/10" : isFullWidth ? "w-full" : "w-auto whitespace-nowrap"} ${iconOnly ? "" : "bg-white hover:bg-slate-50 dark:hover:bg-dark-subtle border"} ${error ? "border-rose-400 !bg-rose-50/50 dark:!bg-rose-950/20" : iconOnly ? "" : "border-slate-200 dark:border-zinc-700"} text-black dark:text-white flex items-center justify-between gap-1.5 transition-all shadow-xs focus:outline-none focus:ring-0 focus:border-slate-200 focus-visible:outline-none focus-visible:ring-0 outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        {LeadingIcon && (
                            <LeadingIcon
                                size={15}
                                className="text-black dark:text-zinc-400 shrink-0"
                            />
                        )}
                        {selectedOption?.icon &&
                            (!selectedOption?.iconPosition ||
                                selectedOption.iconPosition?.length === 0 ||
                                selectedOption.iconPosition === "left") && (
                                <div className="flex items-center shrink-0">
                                    {renderOptionIcon(
                                        selectedOption,
                                        false,
                                        15
                                    )}
                                </div>
                            )}
                        <span
                            className={`${iconOnly ? "sr-only" : "truncate text-left whitespace-nowrap"} ${hasSelection ? "text-slate-900 dark:text-white font-semibold" : placeholderClassName || "text-xs text-slate-400 dark:text-zinc-400"}`}
                        >
                            {displayLabel}
                        </span>
                        {selectedOption?.icon &&
                            selectedOption?.iconPosition &&
                            selectedOption.iconPosition?.length > 0 &&
                            selectedOption.iconPosition !== "left" && (
                                <div className="flex items-center shrink-0">
                                    {renderOptionIcon(
                                        selectedOption,
                                        false,
                                        15
                                    )}
                                </div>
                            )}
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                        {clearable && hasSelection && !disabled && (
                            <span className="w-5" aria-hidden="true" />
                        )}
                        {!noarrow && (
                            <Icon
                                value="chevronDown"
                                size={14}
                                className={`text-slate-400 dark:text-zinc-400 shrink-0 transition-transform duration-200 ${isOpen && !isClosing ? "rotate-180 text-primary dark:text-white" : ""}`}
                            />
                        )}
                    </div>
                </button>
                {clearable && hasSelection && !disabled && (
                    <button
                        type="button"
                        onClick={handleClear}
                        aria-label={t("select.clear")}
                        className="absolute right-6 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10"
                    >
                        <Icon value="x" size={14} />
                    </button>
                )}
            </div>

            {error && typeof error === "string" && (
                <p
                    id={`${listId}-error`}
                    className="mt-1 text-xs text-rose-600 dark:text-rose-400 font-medium"
                >
                    {error}
                </p>
            )}

            {/* Dropdown Menu via Portal so it is never clipped by overflow-x-auto or overflow-hidden */}
            {(isOpen || isClosing) &&
                typeof document !== "undefined" &&
                createPortal(
                    <>
                        <div
                            className="fixed inset-0 z-[120000] bg-transparent cursor-default"
                            data-overlay-owner={owner || undefined}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                closeDropdown();
                            }}
                        />
                        <div
                            ref={dropdownRef}
                            data-overlay-owner={owner || undefined}
                            onKeyDown={handleKeyboard}
                            style={{
                                position: "fixed",
                                zIndex: 120001,
                                ...(dropdownCoords.placement === "top"
                                    ? { bottom: dropdownCoords.bottom }
                                    : { top: dropdownCoords.top }),
                                ...(dropdownCoords.right !== undefined
                                    ? {
                                          right: dropdownCoords.right,
                                          maxWidth: `min(600px, calc(100vw - ${dropdownCoords.right}px - 16px))`,
                                      }
                                    : {
                                          left: dropdownCoords.left,
                                          maxWidth: `min(600px, calc(100vw - ${dropdownCoords.left || 0}px - 16px))`,
                                      }),
                                minWidth: dropdownMinWidth
                                    ? `${dropdownMinWidth}px`
                                    : `${Math.max(120, dropdownCoords.width)}px`,
                                width: "max-content",
                            }}
                            className={`select-dropdown ${animationClass} flex flex-col bg-white dark:bg-dark-box rounded-lg border border-slate-200/90 dark:border-white/10 shadow-2xl overflow-hidden ${dropdownClassName}`}
                        >
                            {searchable && (
                                <div className="p-2 border-b border-slate-100 dark:border-white/10 bg-white dark:bg-dark-box shrink-0 z-10">
                                    <div className="relative">
                                        <Icon
                                            value="search"
                                            size={13}
                                            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                                        />
                                        <input
                                            autoFocus
                                            type="text"
                                            data-transient
                                            role="combobox"
                                            aria-label={
                                                searchPlaceholder ||
                                                label ||
                                                t("common.search")
                                            }
                                            aria-expanded={isOpen}
                                            aria-controls={listId}
                                            aria-activedescendant={
                                                filteredOptions[activeIndex]
                                                    ? `${listId}-option-${activeIndex}`
                                                    : undefined
                                            }
                                            value={searchTerm}
                                            onChange={(e) =>
                                                setSearchTerm(e.target.value)
                                            }
                                            placeholder={
                                                searchPlaceholder ||
                                                t("common.search")
                                            }
                                            className="w-full pl-8 pr-7 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-white/20 bg-white dark:bg-dark-box outline-none text-slate-800 dark:text-zinc-100"
                                        />
                                        {searchTerm && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setSearchTerm("")
                                                }
                                                aria-label={t("select.clear")}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer"
                                            >
                                                <Icon value="x" size={11} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {multiple && options.length > 0 && (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(event) => {
                                        if (
                                            event.key === "Enter" ||
                                            event.key === " "
                                        ) {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            event.currentTarget.click();
                                        }
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const allSelected = options.every((o) =>
                                            selectedValues.some(
                                                (v) =>
                                                    String(v) ===
                                                    String(o.value)
                                            )
                                        );
                                        if (allSelected) {
                                            onChange([]);
                                        } else {
                                            onChange(
                                                options.map((o) => o.value)
                                            );
                                        }
                                    }}
                                    className="flex items-center justify-center gap-2 px-3 py-1.5 border-b border-slate-100 dark:border-white/10 text-xs font-semibold text-slate-700 dark:text-zinc-200 cursor-pointer hover:bg-slate-100 dark:hover:bg-white/10 transition-colors shrink-0 bg-white dark:bg-dark-box"
                                >
                                    <div>{t("common.select_all")}</div>
                                    <div
                                        className="shrink-0 ml-2"
                                        aria-hidden="true"
                                    >
                                        {options.every((option) =>
                                            selectedValues.some(
                                                (selected) =>
                                                    String(selected) ===
                                                    String(option.value)
                                            )
                                        ) ? (
                                            <Icon
                                                value="checkCircle"
                                                size={18}
                                                className="text-primary dark:text-primary-300"
                                            />
                                        ) : (
                                            <div className="h-[18px] w-[18px]" />
                                        )}
                                    </div>
                                </div>
                            )}

                            <div
                                id={listId}
                                role="listbox"
                                aria-label={
                                    label ||
                                    ariaLabel ||
                                    placeholder ||
                                    t("common.select")
                                }
                                aria-multiselectable={multiple || undefined}
                                className="overflow-y-auto max-h-60 p-1 flex flex-col space-y-0.5"
                            >
                                {filteredOptions.length > 0 ? (
                                    filteredOptions.map(
                                        (option, optionIndex) => {
                                            const isSelected = multiple
                                                ? selectedValues.some(
                                                      (v) =>
                                                          String(v) ===
                                                          String(option.value)
                                                  )
                                                : String(option.value) ===
                                                  String(value);

                                            return (
                                                <div
                                                    key={String(option.value)}
                                                    id={`${listId}-option-${optionIndex}`}
                                                    role="option"
                                                    aria-selected={isSelected}
                                                    tabIndex={-1}
                                                    onMouseEnter={() =>
                                                        setActiveIndex(
                                                            optionIndex
                                                        )
                                                    }
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelect(
                                                            option.value
                                                        );
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (
                                                            e.key === "Enter" ||
                                                            e.key === " "
                                                        ) {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleSelect(
                                                                option.value
                                                            );
                                                        }
                                                    }}
                                                    className={`w-full px-3 py-2 text-xs font-medium flex items-center justify-between rounded-lg transition-colors text-left cursor-pointer ${
                                                        isSelected
                                                            ? "bg-primary/10 text-primary font-bold dark:bg-primary/30 dark:text-white"
                                                            : "text-slate-700 dark:text-zinc-200 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white"
                                                    }`}
                                                >
                                                    <div className="flex flex-1 items-center justify-start gap-2 min-w-0">
                                                        {(!option?.iconPosition ||
                                                            option.iconPosition
                                                                ?.length ===
                                                                0 ||
                                                            option.iconPosition ===
                                                                "left") &&
                                                            renderOptionIcon(
                                                                option,
                                                                isSelected,
                                                                14
                                                            )}
                                                        <div className="flex flex-col min-w-0 flex-1">
                                                            <span className="whitespace-normal break-words leading-snug">
                                                                {option.label}
                                                            </span>
                                                            {option.subtitle && (
                                                                <span className="text-[10px] text-slate-400 dark:text-zinc-400 whitespace-normal break-words">
                                                                    {
                                                                        option.subtitle
                                                                    }
                                                                </span>
                                                            )}
                                                        </div>
                                                        {option?.iconPosition &&
                                                            option.iconPosition
                                                                ?.length > 0 &&
                                                            option.iconPosition !==
                                                                "left" &&
                                                            renderOptionIcon(
                                                                option,
                                                                isSelected,
                                                                14
                                                            )}
                                                    </div>

                                                    {multiple ? (
                                                        <div
                                                            className="shrink-0 ml-2"
                                                            onClick={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                        >
                                                            {isSelected ? (
                                                                <Icon
                                                                    value="checkCircle"
                                                                    size={18}
                                                                    className="text-primary dark:text-primary-300"
                                                                />
                                                            ) : (
                                                                <div className="h-[18px] w-[18px]" />
                                                            )}
                                                        </div>
                                                    ) : (
                                                        isSelected && (
                                                            <Icon
                                                                value="check"
                                                                size={14}
                                                                className="text-primary dark:text-white shrink-0 ml-2"
                                                            />
                                                        )
                                                    )}
                                                </div>
                                            );
                                        }
                                    )
                                ) : (
                                    <div className="p-3 text-center text-xs text-slate-400">
                                        {t("common.no_results")}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>,
                    document.body
                )}
        </div>
    );
}

export default Select;
